import { createContext, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import es from "./es";
import en from "./en";

const dictionaries = { es, en };
const LangContext = createContext(null);

// La URL manda: /en/... es inglés, cualquier otra ruta es español.
function langFromPath(pathname) {
  return pathname === "/en" || pathname.startsWith("/en/") ? "en" : "es";
}

export function stripLang(pathname) {
  if (pathname === "/en") return "/";
  if (pathname.startsWith("/en/")) return pathname.slice(3);
  return pathname;
}

function withLang(path, lang) {
  if (lang !== "en") return path;
  return path === "/" ? "/en" : `/en${path}`;
}

// Preferencia guardada (si el usuario ya eligió) o idioma del navegador.
function preferredLang() {
  const saved = localStorage.getItem("lang");
  if (saved === "es" || saved === "en") return saved;
  const nav = (navigator.languages && navigator.languages[0]) || navigator.language || "es";
  return nav.toLowerCase().startsWith("es") ? "es" : "en";
}

const isBot = () => /bot|crawler|spider|crawling/i.test(navigator.userAgent);

export function LangProvider({ children }) {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const lang = langFromPath(pathname);

  // Solo al cargar: si se entra por una ruta sin /en y el visitante prefiere
  // inglés, se le redirige. Los bots no se redirigen para que indexen ambas versiones.
  useEffect(() => {
    if (lang === "es" && preferredLang() === "en" && !isBot()) {
      navigate(withLang(pathname, "en") + search + hash, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = dictionaries[lang].meta.title;
  }, [lang]);

  const setLang = (newLang) => {
    localStorage.setItem("lang", newLang);
    navigate(withLang(stripLang(pathname), newLang) + search + hash);
  };

  const t = (key) =>
    key.split(".").reduce((obj, k) => (obj == null ? obj : obj[k]), dictionaries[lang]) ?? key;

  const localePath = (path) => withLang(path, lang);

  const formatDate = (date, options) =>
    new Date(date).toLocaleDateString(lang === "en" ? "en-US" : "es-ES", options);

  return (
    <LangContext.Provider value={{ lang, setLang, t, localePath, formatDate }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}
